import { ParsedVendorRow } from "./types";

// The canonical column order used when the pasted/uploaded text has no header row.
const DEFAULT_COLUMN_ORDER = [
  "vendorName",
  "accountNumber",
  "ifsc",
  "accountHolderName",
  "emailId",
  "mobileNo",
  "isVirtualAccount",
] as const;

type ColumnKey = (typeof DEFAULT_COLUMN_ORDER)[number];

// Accepts common header spellings so ops can paste straight from a spreadsheet export.
const HEADER_ALIASES: Record<string, ColumnKey> = {
  vendorname: "vendorName",
  vendor: "vendorName",
  name: "vendorName",
  accountnumber: "accountNumber",
  accountno: "accountNumber",
  accno: "accountNumber",
  account: "accountNumber",
  ifsc: "ifsc",
  ifsccode: "ifsc",
  accountholdername: "accountHolderName",
  holdername: "accountHolderName",
  beneficiaryname: "accountHolderName",
  emailid: "emailId",
  email: "emailId",
  mobileno: "mobileNo",
  mobile: "mobileNo",
  phone: "mobileNo",
  isvirtualaccount: "isVirtualAccount",
  virtualaccount: "isVirtualAccount",
  isvirtual: "isVirtualAccount",
  virtual: "isVirtualAccount",
};

// Optional isVirtualAccount cell: blank means not virtual; anything unrecognised is a validation error.
const TRUE_VALUES = ["true", "yes", "y", "1"];
const FALSE_VALUES = ["false", "no", "n", "0", ""];
const parseVirtualFlag = (raw: string): boolean | undefined => {
  const value = raw.trim().toLowerCase();
  if (TRUE_VALUES.includes(value)) return true;
  if (FALSE_VALUES.includes(value)) return false;
  return undefined;
};

const normalizeHeader = (h: string): string => h.trim().toLowerCase().replace(/[\s_-]/g, "");

// Splits a single CSV line, honouring simple double-quote quoting (values may contain commas).
const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  out.push(current);
  return out.map((v) => v.trim());
};

const looksLikeHeader = (cells: string[]): boolean =>
  cells.some((c) => HEADER_ALIASES[normalizeHeader(c)] !== undefined);

const isBlankLine = (line: string): boolean => line.trim().length === 0;

// Soft IFSC sanity check (11 chars, 5th char is 0). Kept as a warning so ops can still override.
const IFSC_REGEX = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

const validateRow = (row: {
  vendorName: string;
  accountNumber: string;
  ifsc: string;
  accountHolderName: string;
  isVirtualAccount: string;
}): string | undefined => {
  const missing: string[] = [];
  if (!row.vendorName) missing.push("vendorName");
  if (!row.accountNumber) missing.push("accountNumber");
  if (!row.ifsc) missing.push("ifsc");
  if (!row.accountHolderName) missing.push("accountHolderName");
  if (missing.length > 0) {
    return `Missing required: ${missing.join(", ")}`;
  }
  if (!IFSC_REGEX.test(row.ifsc)) {
    return "IFSC does not look valid (expected 11 chars, e.g. HDFC0001234)";
  }
  if (parseVirtualFlag(row.isVirtualAccount) === undefined) {
    return "isVirtualAccount must be true or false (or left blank)";
  }
  return undefined;
};

/**
 * Parses pasted text or CSV file content into vendor rows for the import preview.
 * - The first non-blank line is treated as a header when it contains a recognised
 *   column name; otherwise the default column order is assumed.
 * - Blank lines are skipped. Each row carries a local key + 1-based source line number.
 */
export const parseVendorRows = (raw: string): ParsedVendorRow[] => {
  const rows: ParsedVendorRow[] = [];
  if (!raw || !raw.trim()) return rows;

  const lines = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

  // Locate the first non-blank line to decide on a header.
  let firstContentIdx = 0;
  while (firstContentIdx < lines.length && isBlankLine(lines[firstContentIdx])) {
    firstContentIdx += 1;
  }
  if (firstContentIdx >= lines.length) return rows;

  const firstCells = splitCsvLine(lines[firstContentIdx]);
  const hasHeader = looksLikeHeader(firstCells);

  const columnOrder: ColumnKey[] = hasHeader
    ? firstCells.map((c) => HEADER_ALIASES[normalizeHeader(c)] ?? ("" as ColumnKey))
    : [...DEFAULT_COLUMN_ORDER];

  const dataStartIdx = hasHeader ? firstContentIdx + 1 : firstContentIdx;

  for (let i = dataStartIdx; i < lines.length; i += 1) {
    const line = lines[i];
    if (isBlankLine(line)) continue;
    const cells = splitCsvLine(line);

    const record: Record<ColumnKey, string> = {
      vendorName: "",
      accountNumber: "",
      ifsc: "",
      accountHolderName: "",
      emailId: "",
      mobileNo: "",
      isVirtualAccount: "",
    };
    columnOrder.forEach((key, idx) => {
      if (key) record[key] = (cells[idx] ?? "").trim();
    });

    const validationError = validateRow(record);
    rows.push({
      rowKey: `row-${i}`,
      lineNo: i + 1,
      vendorName: record.vendorName,
      accountNumber: record.accountNumber,
      ifsc: record.ifsc,
      accountHolderName: record.accountHolderName,
      emailId: record.emailId || undefined,
      mobileNo: record.mobileNo || undefined,
      isVirtualAccount: parseVirtualFlag(record.isVirtualAccount) === true,
      validationError,
    });
  }

  return rows;
};

// A ready-to-paste sample so ops know the expected column order.
export const SAMPLE_CSV = [
  "vendorName,accountNumber,ifsc,accountHolderName,emailId,mobileNo,isVirtualAccount",
  "HDFC AMC Collection,50200012345678,HDFC0001234,HDFC Asset Management Co Ltd,collections@hdfcfund.com,9876543210,",
  "SBI MF Collection,00000031234567890,SBIN0000456,SBI Funds Management Ltd,,,",
  "MFU Escrow Acme,MFKK1234567890,KKBK0000958,KMB Collection Account MFU,,,true",
].join("\n");
